// This file is for rendering the parameters in the workspace
// The parameters are rendered in the form of list

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  addParameterToChart,
  deleteParameter,
  loadSavedParameter,
  resetParameterSession,
  setParametres,
} from '../../redux/Parameters/ParametersAction';
import { Box, IconButton, List, Tooltip, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { fontSize } from '../..';
import ShortUniqueId from 'short-unique-id';
import { Box as CustomBox } from '../DataViewer/Box';
import {
  AcceptRejectDialog,
  NotificationDialog,
  PopUpSpinner,
} from '../CommonFunctions/DialogComponents';
import { setSelectedControlMenu } from '../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { resetCurrentCalculationSession } from '../../redux/Calculations/CalculationsActions';
import {
  deleteParameterIdFromFilter,
  setFilterCallAPI,
  updateAxesQueryParam,
} from '../../redux/ChartPoperties/ChartPropertiesActions';
import { RootState } from '../../redux';
import { callQueryAPI } from '../../redux/ChartPoperties/ChartControlsActions';
import { getNameFor } from '../Calculations/utils';
import { checkIfMissingColumn } from '../CommonFunctions/CommonFunctions';

const ShowSavedParameters = () => {
  const { tabTileProps, parameters, calculations, chartProperties } = useSelector(
    (state: RootState) => ({
      tabTileProps: state.tabTileProps,
      parameters: state.parameters,
      calculations: state.calculations,
      chartProperties: state.chartProperties,
    }),
  );
  const dispatch = useDispatch();

  const [alert, setAlert] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [heading, setHeading] = useState('');
  const [messages, setMessages] = useState('');
  const refId = useRef<null | string>(null);

  const [warningParameters, setWarningParameters] = useState<{
    [key: string]: boolean;
  }>({});
  const propKey = useMemo(
    () => `${tabTileProps?.selectedTabId}.${tabTileProps?.selectedTileId}`,
    [tabTileProps?.selectedTabId, tabTileProps?.selectedTileId],
  );
  const savedParametersList = useMemo(() => {
    return parameters?.savedParameters;
  }, [parameters?.savedParameters]);

  const realSelectedDatasetId = chartProperties.properties[propKey]?.selectedDs.id;

  const currentCalculationSession = calculations?.properties[propKey]?.currentCalculationSession;
  const vendor = chartProperties.properties[propKey]?.selectedDs?.vendor;

  const currentParameterSession: any = useMemo(() => {
    return parameters.properties[propKey]?.parameterSession;
  }, [parameters.properties[propKey]?.parameterSession]);

  const handleCreateParameter = (session: any, isNew: boolean = false) => {
    if (session) {
      setHeading('CALCULATION IN PROGRESS');
      setMessages(
        'You have an unsaved calculation session open. If you proceed, the current calculation will be discarded. Do you want to proceed?',
      );
      setOpen(true);
      return;
    }
    if (isNew) {
      setHeading('PARAMETER IN PROGRESS');
      setMessages(
        'You have an unsaved parameter session open. If you proceed, the current parameter will be discarded. Do you want to proceed?',
      );
      setOpen(true);
      return;
    }
    const uid = new ShortUniqueId({ length: 6 });
    const parameterId = uid();
    // generate name according to the number of parameters saved in the state
    const parameterName = getNameFor(
      'parameter',
      savedParametersList,
      ['oracle', 'snowflake'].includes(vendor),
    );
    dispatch(setParametres(propKey, realSelectedDatasetId, parameterName, parameterId));
  };

  const handleEditParameter = (parameterId: string) => {
    if (currentParameterSession) {
      setAlert({
        severity: 'warning',
        message:
          'You have an unsaved parameter session open. save or close your current parameter before editing or creating a parameter',
        show: true,
      });
      return;
    }
    dispatch(loadSavedParameter(propKey, parameterId));
  };

  const handleDeleteParameter = (parameterId: string) => {
    const isLinkedWithChartFilter = parameters.savedParameters.find(
      (parameter: any) => parameter.parameterId === parameterId,
    )?.chartFilter;
    const isLinkedWithCalculation = parameters.savedParameters.find(
      (parameter: any) => parameter.parameterId === parameterId,
    )?.calculationId;
    refId.current = parameterId;

    if (isLinkedWithCalculation && isLinkedWithCalculation.length > 0) {
      setAlert({
        severity: 'warning',
        message:
          'This parameter is currently being used in a calculation. Please delete the calculation and try again.',
      });
      return;
    }

    if (isLinkedWithChartFilter && isLinkedWithChartFilter.length > 0) {
      setHeading('ALREADY IN USE');
      setMessages(
        'This parameter is already linked with a chart filter. If you proceed, the chart filter under this parameter will affect the chart. Do you want to proceed?',
      );
      setOpen(true);
      return;
    }

    dispatch(deleteParameter(propKey, parameterId as string));
  };

  const handleAddParameter = (parameterId: string) => {
    refId.current = parameterId;
    if (currentCalculationSession) {
      setHeading('UNSAVED CALCULATION');
      setMessages(
        'You have an unsaved calculation session open. If you proceed, the current calculation will be discarded. Do you want to proceed?',
      );
      setOpen(true);
      return;
    }
    if (
      !parameters.savedParameters.find((parameter: any) => parameter.parameterId === parameterId)
        ?.showParameter
    ) {
      dispatch(addParameterToChart(propKey, parameterId));
    }
    if (tabTileProps.selectedControlMenu !== 'Report Filters') {
      dispatch(setSelectedControlMenu('Report Filters'));
    }
  };

  const handleAcceptChanges = () => {
    if (heading === 'PARAMETER IN PROGRESS') {
      setLoading(true);
      dispatch(resetParameterSession(propKey));
      setTimeout(() => {
        setLoading(false);
        setOpen(false);
        handleCreateParameter(false, false);
      }, 100);
      return;
    }
    if (heading === 'CALCULATION IN PROGRESS') {
      setLoading(true);
      dispatch(resetCurrentCalculationSession(propKey));
      setTimeout(() => {
        setLoading(false);
        setOpen(false);
        handleCreateParameter(false);
      }, 100);
      return;
    } else if (heading === 'UNSAVED CALCULATION') {
      setLoading(true);
      dispatch(resetCurrentCalculationSession(propKey));
      setTimeout(() => {
        setLoading(false);
        handleAddParameter(refId.current!);
        setOpen(false);
      }, 100);
      return;
    } else {
      setLoading(true);
      const fields = chartProperties.properties[propKey].chartAxes[0].fields;
      const filterFieldData = fields.find(
        (fieldData: any) => fieldData.parameterId === refId.current,
      );
      const parameter = parameters.savedParameters.find(
        (parameter: any) => parameter.parameterId === refId.current,
      );
      // if the filter is deleted from the chart, then delete the parameter
      if (!filterFieldData) {
        dispatch(deleteParameter(propKey, refId.current as string));
        setOpen(false);
        setLoading(false);
        return;
      }
      const chartFilter = parameter?.chartFilter?.find(
        (filter: any) => filter.chartFilterId === filterFieldData.uId,
      );
      if (!chartFilter) return;
      dispatch(
        deleteParameterIdFromFilter(
          propKey,
          0,
          chartFilter.currentChartAxesName,
          refId.current as string,
        ),
      );
      dispatch(deleteParameter(propKey, refId.current as string));
      setTimeout(() => {
        setOpen(false);
        setLoading(false);
      }, 100);
      filterFieldData.userSelection = [];
      filterFieldData.exprInput = '';
      filterFieldData.callAPI = true;
      dispatch(
        updateAxesQueryParam(
          propKey,
          0,
          chartFilter.itemIndex,
          filterFieldData,
          chartFilter.currentChartAxesName,
        ),
      );
      if (chartFilter.propKey === propKey) {
        dispatch(callQueryAPI(propKey, true));
      }
      let callAPIDashboard = false;
      chartProperties.propList[1].forEach((tabTileId: string) => {
        chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
          if (field.parameterId === parameter?.parameterId) {
            dispatch(callQueryAPI(tabTileId, true));
            callAPIDashboard = true;
          }
        });
      });
      setTimeout(() => {
        //wait for the chart to render before setting the callAPI to false otherwise due to the async nature of the chart rendering, the callAPI is set to true before the chart is rendered
        dispatch(setFilterCallAPI(propKey, chartFilter.itemIndex, false));
      }, 100);
    }
  };

  const renderInBlocks = (arr: any[], blockSize: number) => {
    if (!arr || arr.length === 0) return [];
    const blocks = [];
    for (let i = 0; i < arr.length; i += blockSize) {
      blocks.push(arr.slice(i, i + blockSize));
    }
    return blocks;
  };

  const renderBlocks = renderInBlocks(savedParametersList, 4);

  const checkMissingColumns = async () => {
    let missingColumns: {
      [key: string]: boolean;
    } = {};
    const savedParameters = parameters.savedParameters;
    const promises = savedParameters.map(async (parameter: any) => {
      const field = parameter.columns;
      if (!field) return;
      const parameterId = parameter.parameterId;
      // to avoid checking for missing columns in input (single selection) fields and parameters with no column used.
      if (field.displayType === 'input (single selection)' || !field.tableId) {
        missingColumns = {
          ...missingColumns,
          [parameterId]: false,
        };
      } else {
        const results = await checkIfMissingColumn(
          field,
          chartProperties.properties[propKey]?.selectedDs,
          tabTileProps.tablesForSelectedDataSets[realSelectedDatasetId],
        );
        missingColumns = {
          ...missingColumns,
          [parameterId]: results.isMissing,
        };
      }
    });
    await Promise.all(promises);
    setWarningParameters(missingColumns);
  };

  useEffect(() => {
    checkMissingColumns();
  }, [currentParameterSession]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        flexDirection: 'column',
        borderLeft: '2px solid rgba(224, 224, 224, 1)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          px: '0.75rem',
          pt: '0.55rem',
          pb: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 'bold',
            color: 'primary.contrastText',
            fontSize: fontSize.large,
            flexShrink: 0,
          }}
        >
          Parameters
        </Typography>
        <Tooltip title={<span style={{ fontSize: '12px' }}>New parameter</span>}>
          <IconButton
            onClick={() =>
              handleCreateParameter(currentCalculationSession, currentParameterSession)
            }
            sx={{
              color: '#2BB9BB',
              backgroundColor: '#fff',
              padding: '0.2rem',
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(43, 185, 187, 0.15)',
              transition: 'background 0.2s, box-shadow 0.2s',
              outline: '1px solid #2BB9BB',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(43, 185, 187, 0.25)',
                backgroundColor: '#2BB9BB',
                color: '#fff',
                outline: '1px solid #2BB9BB',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '14px',
              },

              '&:focus-visible': {
                outline: '2px solid #2BB9BB',
              },
            }}
            size="small"
          >
            <Add fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          minHeight: 0,
          width: '100%',
          ml: '0.25rem',
          position: 'relative',
          pb: 0,
        }}
      >
        <List
          sx={{
            width: '100%',
            flexGrow: 1,
            overflowY: 'hidden',
            overflowX: renderBlocks.length > 3 ? 'auto' : 'hidden',
            minHeight: 0,
            px: '0.5rem',
            pl: savedParametersList.length < 5 ? '0.1rem' : '0.5rem',
            pb: 0,
            m: 0,
            scrollMarginTop: '12px',
            '&::-webkit-scrollbar': {
              width: '6px',
              height: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ccc',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '8px',
            },
          }}
        >
          {savedParametersList.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-around',
                height: '100%',
                py: '1rem',
              }}
            >
              <Typography
                sx={{
                  color: '#5d5c5c',
                  fontSize: fontSize.medium,
                  width: '100%',
                  textAlign: 'center',
                  mb: '1rem',
                }}
              >
                No saved parameters in the dataset{' '}
                <Box
                  component="span"
                  sx={{
                    maxWidth: '7rem',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={chartProperties?.properties[propKey]?.selectedDs?.datasetName}
                >
                  {chartProperties?.properties[propKey]?.selectedDs?.datasetName}
                </Box>
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.5rem',
                p: 0,
                pr: '0.45rem',
                minWidth: '100%',
                height: '100%',
                overflowY: 'hidden',
                overflowX: renderBlocks.length > 3 ? 'visible' : 'hidden',
                scrollMarginTop: '12px',
                '&::-webkit-scrollbar': {
                  width: '6px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '8px',
                },
              }}
            >
              {renderBlocks.map((block: any[], blockIndex: number) => (
                <Box
                  key={blockIndex}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    minWidth: savedParametersList.length > 4 ? '11.8rem' : '100%',
                    flexShrink: 0,
                    height: 'fit-content',
                  }}
                >
                  {block.map((parameter: any) => (
                    <Box
                      key={parameter.parameterId}
                      sx={{
                        width: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <CustomBox
                        name={parameter?.name}
                        type="parameter"
                        fieldData={parameter}
                        colsOnly={true}
                        warning={warningParameters?.[parameter.parameterId] ?? false}
                        isSavedCalculation={true}
                        handleEditButton={() => {
                          if (currentCalculationSession) {
                            setAlert({
                              severity: 'warning',
                              message:
                                'You have an unsaved calculation session open. Please save or close your current calculation before editing or creating a parameter.',
                            });
                            return;
                          }
                          handleEditParameter(parameter.parameterId);
                        }}
                        handleDeleteButton={() => handleDeleteParameter(parameter.parameterId)}
                        handleAddParameter={() => handleAddParameter(parameter.parameterId)}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </List>
      </Box>

      {open && (
        <AcceptRejectDialog
          open={open}
          closeFunction={() => setOpen(false)}
          acceptFunction={handleAcceptChanges}
          heading={heading}
          messages={[
            {
              text: messages,
            },
          ]}
          rejectFunction={() => setOpen(false)}
          acceptText="Yes"
          rejectText="No"
        />
      )}

      {loading && <PopUpSpinner show={loading} />}

      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
    </Box>
  );
};

export default ShowSavedParameters;
